import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import classes from './TimedRedirection.module.scss';

const TimedRedirection = ({
    delayedTimer = false,
    redirectionUrl = '',
    onRedirect,
    onCancel,
    holdDuration = 1500,
    showCountdown = true,
    showCancelButton = false,
}) => {
    const totalSeconds = delayedTimer ? 60 : 5;
    const [countdown, setCountdown] = useState(totalSeconds);
    const [progress, setProgress] = useState(0);
    const [isVisible, setIsVisible] = useState(false);
    const [isActive, setIsActive] = useState(true);
    const [isCancelled, setIsCancelled] = useState(false);

    useEffect(() => {
        if (!isActive || isCancelled) return;

        const tickInterval = setInterval(() => {
            setCountdown((prev) => {
                if (prev <= 1) {
                    clearInterval(tickInterval);
                    setCountdown(0);
                    setProgress(100);
                    setIsActive(false);
                    setIsVisible(true);

                    // UX delay before redirect
                    setTimeout(() => {
                        if (isCancelled) return;

                        if (typeof onRedirect === 'function') {
                            onRedirect();
                        } else if (redirectionUrl) {
                            window.location.href = redirectionUrl;
                        }
                    }, holdDuration);

                    return 0;
                }

                const next = prev - 1;
                setProgress(((totalSeconds - next) / totalSeconds) * 100);
                return next;
            });
        }, 1000);

        return () => clearInterval(tickInterval);
    }, [isActive, totalSeconds, redirectionUrl, onRedirect, holdDuration, isCancelled]);

    const handleCancel = () => {
        setIsCancelled(true);
        setIsActive(false);
        setIsVisible(false);
        if (typeof onCancel === 'function') onCancel();
    };

    const strokeDasharray = 113;
    const strokeDashoffset = strokeDasharray - (progress / 100) * strokeDasharray;

    return (
        <div className={classes.timedRedirection}>
            {/* Countdown Circle */}
            {showCountdown && countdown > 0 && !isCancelled && (
                <div className={classes.timedRedirection__countdown}>
                    <div className={classes.timedRedirection__number} aria-live="polite">
                        {countdown}
                    </div>
                    <svg className={classes.timedRedirection__svg}>
                        <circle className={classes.timedRedirection__circleBg} r="18" cx="20" cy="20" />
                        <circle
                            className={classes.timedRedirection__circleProgress}
                            r="18"
                            cx="20"
                            cy="20"
                            style={{ strokeDashoffset }}
                        />
                    </svg>
                </div>
            )}

            {/* Redirection Message (auto-visible after timer ends) */}
            {isVisible && !isCancelled && (
                <div className={classes.timedRedirection__linkContainer}>
                    <p>
                        If you are not automatically redirected to the merchant page,&nbsp;
                        <a
                            href={redirectionUrl}
                            target="_parent"
                            rel="noopener noreferrer"
                            className={classes.timedRedirection__link}
                        >
                            click here
                        </a>
                        .
                    </p>
                </div>
            )}

            {/* Optional Cancel Button */}
            {showCancelButton && !isCancelled && isActive && countdown > 0 && (
                <button type="button" className={classes.timedRedirection__cancelButton} onClick={handleCancel}>
                    Cancel Redirection
                </button>
            )}

            {/* Cancelled State */}
            {isCancelled && (
                <div className={classes.timedRedirection__cancelled}>
                    <p>Redirection cancelled.</p>
                </div>
            )}
        </div>
    );
};

TimedRedirection.propTypes = {
    delayedTimer: PropTypes.bool,
    redirectionUrl: PropTypes.string,
    onRedirect: PropTypes.func,
    onCancel: PropTypes.func,
    holdDuration: PropTypes.number,
    showCountdown: PropTypes.bool,
    showCancelButton: PropTypes.bool,
};

export default TimedRedirection;
